// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { brokeredPreviewStorage } from './previewAuthStorage';
import { handleSessionExpired } from '@/lib/sessionExpired';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

function safeSupabaseEndpoint(input: RequestInfo | URL): {
  service: string;
  resource: string;
} | null {
  try {
    const rawUrl = input instanceof Request ? input.url : input.toString();
    const url = new URL(rawUrl, SUPABASE_URL);
    if (url.origin !== new URL(SUPABASE_URL).origin) return null;

    const segments = url.pathname.split('/').filter(Boolean);
    if (segments[0] === 'rest' && segments[1] === 'v1') {
      if (segments[2] === 'rpc' && segments[3]) {
        return { service: 'postgrest', resource: `rpc:${segments[3]}` };
      }
      return segments[2]
        ? { service: 'postgrest', resource: `table:${segments[2]}` }
        : { service: 'postgrest', resource: 'unknown' };
    }
    if (segments[0] === 'functions' && segments[1] === 'v1') {
      const name = segments[2] ?? 'unknown';
      return { service: 'edge_function', resource: `function:${name}` };
    }
    if (segments[0] === 'auth' && segments[1] === 'v1') {
      return { service: 'auth', resource: `auth:${segments[2] ?? 'unknown'}` };
    }
    if (segments[0] === 'storage' && segments[1] === 'v1') {
      return { service: 'storage', resource: `storage:${segments[2] ?? 'unknown'}` };
    }
    return null;
  } catch {
    return null;
  }
}

let writing401Diagnostic = false;

async function diagnosticFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 401) return response;

  const endpoint = safeSupabaseEndpoint(input);
  // The diagnostic writer itself uses an Edge Function. Exclude it to avoid
  // recursive logging if that function is unavailable or rejects auth.
  if (!endpoint || endpoint.resource === 'function:record-client-diagnostic' || writing401Diagnostic) return response;

  const request = input instanceof Request ? input : null;
  const method = (init?.method ?? request?.method ?? 'GET').toUpperCase();
  void response.clone().json().then((body: unknown) => {
    const code = body && typeof body === 'object' && 'code' in body &&
      typeof (body as { code?: unknown }).code === 'string'
      ? (body as { code: string }).code.slice(0, 80)
      : null;
    writing401Diagnostic = true;
    void import('@/lib/portalAuthDiagnostics')
      .then(({ logPortalAuthDiagnostic }) => logPortalAuthDiagnostic('supabase_request_401', {
        ...endpoint,
        method,
        status: response.status,
        error_code: code,
      }))
      .catch(() => undefined)
      .finally(() => { writing401Diagnostic = false; });
  }).catch(() => {
    writing401Diagnostic = true;
    void import('@/lib/portalAuthDiagnostics')
      .then(({ logPortalAuthDiagnostic }) => logPortalAuthDiagnostic('supabase_request_401', {
        ...endpoint,
        method,
        status: response.status,
        error_code: null,
      }))
      .catch(() => undefined)
      .finally(() => { writing401Diagnostic = false; });
  });

  return response;
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  global: { fetch: diagnosticFetch },
  auth: {
    storage: brokeredPreviewStorage(),
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  }
});

// ---------------------------------------------------------------------------
// Conservative 401 handling for edge function calls.
//
// Goals:
//  - Do NOT sign the user out on a transient 401 (autoRefresh race, network blip).
//  - Do NOT sign the user out on payload/string-level "Unauthorized" (those are
//    typically RBAC/tenant denials surfaced by edge functions, not session loss).
//  - DO attempt a single session refresh + single retry of the failing call.
//  - DO sign out only when refresh genuinely fails (token revoked / missing).
//
// Loop / storm guarantees:
//  - ensureFreshSession() dedupes concurrent refreshes via a single in-flight
//    promise, and caches the last nonterminal outcome for COOLDOWN_MS so a burst of 401s
//    triggers at most one refresh.
//  - The one retry calls the original SDK method directly, so a second 401
//    returns to the caller without another refresh or retry.
//  - supabase.auth.refreshSession() hits the GoTrue /token endpoint directly,
//    NOT functions.invoke, so this interceptor is never re-entered by the
//    refresh itself. This is the recursion guard.
// ---------------------------------------------------------------------------

type RefreshOutcome = 'refreshed' | 'terminal' | 'indeterminate';

const COOLDOWN_MS = 5000;
let inflightRefresh: Promise<RefreshOutcome> | null = null;
let lastOutcome: { outcome: RefreshOutcome; at: number } | null = null;

// A new or refreshed auth session invalidates any cached 401 recovery outcome.
// This lets the next request recover immediately after login or SDK auto-refresh.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
    lastOutcome = null;
  }
});

async function ensureFreshSession(): Promise<RefreshOutcome> {
  const now = Date.now();
  if (lastOutcome && now - lastOutcome.at < COOLDOWN_MS) {
    return lastOutcome.outcome;
  }
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async (): Promise<RefreshOutcome> => {
    try {
      // Direct GoTrue call — does NOT route through functions.invoke,
      // so the interceptor below cannot be re-entered here.
      const { data, error } = await supabase.auth.refreshSession();
      const outcome: RefreshOutcome = data?.session
        ? 'refreshed'
        : isTerminalRefreshError(error)
          ? 'terminal'
          : 'indeterminate';
      const code = error && typeof error.code === 'string' && /^[a-z0-9_-]{1,80}$/i.test(error.code)
        ? error.code
        : null;
      void import('@/lib/portalAuthDiagnostics').then(({ logPortalAuthDiagnostic }) =>
        logPortalAuthDiagnostic('supabase_session_recovery', {
          reason: outcome === 'refreshed' ? 'refresh_succeeded' :
            outcome === 'terminal' ? 'refresh_terminal_failure' : 'refresh_indeterminate',
          source: 'edge_function_401_recovery',
          refresh_error_code: code,
        }),
      ).catch(() => undefined);
      return outcome;
    } catch (error) {
      const outcome: RefreshOutcome = isTerminalRefreshError(error) ? 'terminal' : 'indeterminate';
      const code = error && typeof error === 'object' && 'code' in error &&
        typeof (error as { code?: unknown }).code === 'string' &&
        /^[a-z0-9_-]{1,80}$/i.test((error as { code: string }).code)
        ? (error as { code: string }).code
        : null;
      void import('@/lib/portalAuthDiagnostics').then(({ logPortalAuthDiagnostic }) =>
        logPortalAuthDiagnostic('supabase_session_recovery', {
          reason: outcome === 'terminal' ? 'refresh_terminal_failure' : 'refresh_indeterminate',
          source: 'edge_function_401_recovery',
          refresh_error_code: code,
        }),
      ).catch(() => undefined);
      return outcome;
    } finally {
      // Settle cooldown cache after the promise resolves below.
    }
  })().then((outcome) => {
    // Do not cache terminal failures across a fresh sign-in. A user may
    // immediately authenticate again after this refresh proves old credentials
    // were revoked.
    lastOutcome = outcome === 'terminal' ? null : { outcome, at: Date.now() };
    inflightRefresh = null;
    return outcome;
  });

  return inflightRefresh;
}

const originalInvoke: typeof supabase.functions.invoke =
  supabase.functions.invoke.bind(supabase.functions);

supabase.functions.invoke = (async (...args: Parameters<typeof supabase.functions.invoke>) => {
  const [fnName, options] = args as [string, any];

  const result = await originalInvoke(fnName, options);

  const status = (result.error as any)?.context?.status;
  const isHttp401 = status === 401;

  if (!isHttp401) {
    return result;
  }

  const outcome = await ensureFreshSession();

  if (outcome === 'refreshed') {
    // Call the original SDK method directly so this retry cannot re-enter
    // the interceptor or trigger a second refresh/retry loop.
    return originalInvoke(fnName, options);
  }

  // A request-level 401 is not enough to establish that the session expired.
  // Sign out only when GoTrue confirms the refresh token is terminally invalid.
  if (outcome === 'terminal') {
    void handleSessionExpired(() =>
      supabase.auth.signOut({ scope: 'local' })
    );
  }
  return result;
}) as typeof supabase.functions.invoke;

function isTerminalRefreshError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const authError = error as { code?: unknown; message?: unknown; name?: unknown };
  // AuthRetryableFetchError includes network failures and retryable Auth
  // service errors. These must never clear the user's local session.
  if (authError.name === 'AuthRetryableFetchError') return false;

  const text = `${String(authError.code ?? '')} ${String(authError.message ?? '')}`.toLowerCase();
  return [
    'invalid_grant',
    'refresh_token_not_found',
    'refresh_token_already_used',
    'session_not_found',
    'session_missing',
    'auth session missing',
    'invalid refresh token',
    'refresh token not found',
  ].some((pattern) => text.includes(pattern));
}
